import {
  useState,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
  memo,
} from "react"
import { useChatDispatch } from "./context"
import { useInputHistory } from "./useInputHistory"
import type { ChatTransport } from "../transport/types"
import { arrowUpCircleFill, stopCircleFill } from "../utils/icons"

export interface ChatInputProps {
  transport: ChatTransport
  inputId: string
  disabled: boolean
  hasTopShadow?: boolean
  placeholder: string
  onSend?: () => void
  userMessages: string[]
  enableCancel?: boolean
  cancelRequested?: boolean
  isStreaming?: boolean
  onCancel?: () => void
}

export interface ChatInputHandle {
  setInputValue(
    value: string,
    options?: { submit?: boolean; focus?: boolean },
  ): void
  focus(): void
}

export const ChatInput = memo(
  forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput(
    {
      transport,
      inputId,
      disabled,
      hasTopShadow = false,
      placeholder,
      onSend,
      userMessages,
      enableCancel,
      cancelRequested,
      isStreaming,
      onCancel,
    },
    ref,
  ) {
    const dispatch = useChatDispatch()

    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const isComposingRef = useRef(false)
    const [hasText, setHasText] = useState(false)
    const { recall, reset, isActive } = useInputHistory(userMessages)

    function updateHeight(el: HTMLTextAreaElement): void {
      if (el.scrollHeight === 0) return
      el.style.height = "auto"
      el.style.height = `${el.scrollHeight}px`
    }

    const sendInput = useCallback(
      (focusAfter = true): void => {
        const el = textareaRef.current
        if (!el) return
        const content = el.value
        if (content.trim().length === 0) return
        if (disabled) return

        dispatch({ type: "INPUT_SENT", content, role: "user" })
        transport.sendInput(inputId, content)
        onSend?.()

        // Clear the DOM element directly (textarea is fully uncontrolled)
        el.value = ""
        setHasText(false)
        updateHeight(el)
        reset()

        if (focusAfter) el.focus()
      },
      [disabled, dispatch, transport, inputId, onSend, reset],
    )

    const onKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
        const isEnter = e.code === "Enter" && !e.shiftKey
        const el = textareaRef.current
        if (!el) return

        const isUp = e.code === "ArrowUp"
        const isDown = e.code === "ArrowDown"

        const atEnd = el.selectionStart === el.value.length
        const canRecall = isActive() ? atEnd : el.value.length === 0

        if ((isUp || isDown) && canRecall && !isComposingRef.current) {
          const value = recall(isUp ? "up" : "down", el.value)
          if (value !== undefined) {
            e.preventDefault()
            el.value = value
            updateHeight(el)
            setHasText(value.trim().length > 0)
            el.setSelectionRange(value.length, value.length)
          }
          return
        }

        if (isEnter && !isComposingRef.current && el.value.trim().length > 0) {
          e.preventDefault()
          sendInput()
        }
      },
      [sendInput, recall, isActive],
    )

    const onInput = useCallback((): void => {
      const el = textareaRef.current
      if (!el) return
      updateHeight(el)
      setHasText(el.value.trim().length > 0)
    }, [])

    const onCompositionStart = useCallback((): void => {
      isComposingRef.current = true
    }, [])

    const onCompositionEnd = useCallback((): void => {
      isComposingRef.current = false
    }, [])

    useImperativeHandle(
      ref,
      () => ({
        setInputValue(
          newValue: string,
          {
            submit = false,
            focus = false,
          }: { submit?: boolean; focus?: boolean } = {},
        ): void {
          const el = textareaRef.current
          if (!el) return

          const oldValue = el.value
          el.value = newValue
          setHasText(newValue.trim().length > 0)
          updateHeight(el)

          if (submit) {
            // Server-triggered submit still respects the disabled guard
            // (we only skip sendInput() to avoid its focus/clear side-effects).
            if (!disabled) {
              const submitContent = el.value
              if (submitContent.trim().length > 0) {
                dispatch({
                  type: "INPUT_SENT",
                  content: submitContent,
                  role: "user",
                })
                transport.sendInput(inputId, submitContent)
                onSend?.()
                reset()
              }
            }
            // Always restore old value (the submitted value was temporary)
            el.value = oldValue
            setHasText(oldValue.trim().length > 0)
            updateHeight(el)
          }

          if (focus) {
            el.focus()
          }
        },
        focus(): void {
          textareaRef.current?.focus()
        },
      }),
      [disabled, dispatch, transport, inputId, onSend, reset],
    )

    const sendButtonDisabled = disabled || !hasText
    const showStopButton = enableCancel && isStreaming

    return (
      <>
        <textarea
          ref={textareaRef}
          id={inputId}
          className={hasTopShadow ? "form-control shadow" : "form-control"}
          rows={1}
          placeholder={placeholder}
          aria-disabled={disabled || undefined}
          onKeyDown={onKeyDown}
          onInput={onInput}
          onCompositionStart={onCompositionStart}
          onCompositionEnd={onCompositionEnd}
          aria-label="Chat message"
          data-shiny-no-bind-input
        />
        {showStopButton ? (
          <button
            type="button"
            className="shiny-chat-btn-send shiny-chat-btn-cancel"
            title="Stop generating"
            aria-label="Stop generating"
            disabled={cancelRequested}
            onClick={onCancel}
            dangerouslySetInnerHTML={{ __html: stopCircleFill }}
          />
        ) : (
          <button
            type="button"
            className="shiny-chat-btn-send"
            title="Send message"
            aria-label="Send message"
            disabled={sendButtonDisabled}
            onClick={() => sendInput()}
            dangerouslySetInnerHTML={{ __html: arrowUpCircleFill }}
          />
        )}
      </>
    )
  }),
)
